import { useRef } from 'react'

/**
 * Input de dinero con separador de miles en vivo (formato en-US: coma para los
 * miles, punto para los decimales — el mismo que usa `fmtMonto`). Guarda hacia
 * afuera el valor "crudo" (solo dígitos y punto, sin comas) para que el resto
 * del código lo siga parseando con `parseMonto`/`parseFloat` sin cambios.
 *
 * Preserva la posición del cursor contando los dígitos a su izquierda, así se
 * puede editar en el medio del número sin que el caret salte al final.
 */

// "1234567.8" → "1,234,567.8" (agrupa la parte entera, deja los decimales como están)
function agrupar(raw) {
  if (raw === '' || raw == null) return ''
  const s = String(raw)
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const i = abs.indexOf('.')
  const ent = (i === -1 ? abs : abs.slice(0, i)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return (neg ? '-' : '') + (i === -1 ? ent : `${ent}.${abs.slice(i + 1)}`)
}

// Deja solo dígitos + un punto decimal, máximo 2 decimales. Con permitirNegativo,
// conserva un "-" adelante (caso: línea de devolución en un recibo mixto +/−).
function crudo(s, permitirNegativo = false) {
  const str = String(s)
  const neg = permitirNegativo && str.trimStart().startsWith('-')
  let v = str.replace(/[^\d.]/g, '')
  const i = v.indexOf('.')
  if (i !== -1) {
    v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, '').slice(0, 2)
  }
  // El "-" solo (sin dígitos todavía) se conserva: es el primer keystroke de
  // un número negativo — descartarlo hacía imposible EMPEZAR tipeando el signo.
  return neg ? '-'.concat(v) : v
}

export default function MoneyInput({ value, onChange, permitirNegativo = false, ...rest }) {
  const ref = useRef(null)

  const handleChange = (e) => {
    const el = e.target
    const caret = el.selectionStart ?? el.value.length
    // dígitos a la izquierda del cursor en el texto recién tipeado
    const digitosIzq = el.value.slice(0, caret).replace(/\D/g, '').length

    const raw = crudo(el.value, permitirNegativo)
    onChange(raw)

    // reposicionar el cursor tras el reformateo
    requestAnimationFrame(() => {
      if (!ref.current) return
      const fmt = agrupar(raw)
      let pos = 0
      let vistos = 0
      while (pos < fmt.length && vistos < digitosIzq) {
        if (/\d/.test(fmt[pos])) vistos++
        pos++
      }
      // Recién tipeado el "-" (sin dígitos aún): el cursor va DESPUÉS del
      // signo, si no el próximo dígito caería adelante del menos.
      if (pos === 0 && fmt.startsWith('-') && caret > 0) pos = 1
      ref.current.setSelectionRange(pos, pos)
    })
  }

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={agrupar(value)}
      onChange={handleChange}
      {...rest}
    />
  )
}
